<script setup lang="ts">
import {computed} from 'vue';
import {pagesPath, AccountRouteName} from '@/ts/router/pages-path';
import DrawerOnMobile from '@/vue/molecules/drawer-on-mobile.vue';
import {useDrawerStore} from '@/ts/store/drawer-store';
import {storeToRefs} from 'pinia';
import UserAvatar from '@/vue/molecules/user-avatar.vue';
import FlatIconBtn from '@/vue/molecules/flat-icon-btn.vue';
import {useAlert} from '@/ts/composables/pure/use-alert';
import {useI18n} from 'vue-i18n';
import {useQuasar} from 'quasar';
import {useSignOut} from '@/ts/composables/stateful/use-auth';
import {IS_DEBUG, SHOW_PAYMENT_FEATURES} from '@/ts/utils/consts';
import {useAccountRoute} from '@/ts/composables/stateful/use-account-route';

const {toAccount, isActive} = useAccountRoute();
const {t: $t} = useI18n();

const emit = defineEmits<{
  requestOpenerFocus: [],
}>();

const drawerStore = useDrawerStore();
const {isUserDrawerOpen} = storeToRefs(drawerStore);

function close(): void {
  drawerStore.closeUserDrawer();
  emit('requestOpenerFocus');
}

interface MenuItem {
  name: AccountRouteName;
  label: string;
  show: boolean;
}

const menuItems = computed(() => {
  const items: MenuItem[] = [
    {name: AccountRouteName.profile, label: $t('profile'), show: true},
    {name: AccountRouteName.subscription, label: $t('current_plan'), show: SHOW_PAYMENT_FEATURES === true},
    {name: AccountRouteName.security, label: $t('security'), show: true},
    {name: AccountRouteName.preferences, label: $t('preferences'), show: true},
    {name: AccountRouteName.badges, label: $t('badges'), show: true},
    {name: AccountRouteName.debug, label: 'Debug', show: IS_DEBUG},
  ];
  return items.filter(item => item.show);
});

function onItemClick(name: AccountRouteName, event: Event): void {
  if (isActive(name)) {
    event.preventDefault();
    close();
  }
}

const $q = useQuasar();
const {showConfirmDialog} = useAlert();
const signOut = useSignOut();

async function showSignOut(): Promise<void> {
  drawerStore.closeUserDrawer();
  try {
    await showConfirmDialog($t('you_are_about'), {
      okLabel: $t('sign_out'),
    });
    $q.loading.show();
    await signOut(pagesPath.public.signUp);
  } catch {/* user clicked cancel */} finally {
    $q.loading.hide();
  }
}

</script>

<template>
  <drawer-on-mobile :is-open="isUserDrawerOpen" :align-right="true" :aria-label="$t('user_menu_title')" @swipe-or-esc-to-close="close">
    <div class="menu" :class="{'menu--is-closed': !isUserDrawerOpen}">
      <div class="mobile-only-drawer-header">
        <user-avatar/>
        <flat-icon-btn
          icon="x-close.svg"
          :label="$t('aria_close_user_menu')"
          @click="close"
        />
      </div>
      <!-- Quasar 2.28 dropped the default role on QMenu; the list is the menu and its
           clickable items (links and the sign-out button) derive role="menuitem" from it -->
      <q-list class="list" role="menu">
        <q-item
          v-for="item in menuItems"
          :key="item.name"
          clickable
          :to="toAccount(item.name)"
          class="list-item"
          :class="{'list-item--active': isActive(item.name)}"
          @click="onItemClick(item.name, $event)"
        >
          {{ item.label }}
        </q-item>
        <q-item clickable class="list-item sign-out" @click="showSignOut">
          {{ $t('sign_out') }}
        </q-item>
      </q-list>
    </div>
  </drawer-on-mobile>
</template>
<style lang="scss" scoped>
.menu {
  position: relative;
  z-index: 1;

  &--is-closed {
    display: none;
    @include screen-lg {
      display: block;
    }
  }
}

.mobile-only-drawer-header {
  display: flex;
  justify-content: space-between;
  height: 50px;
  align-items: center;
  padding: 0 var(--Spacing-spacing-3);
  margin-bottom: var(--Spacing-spacing-4);

  @include screen-lg {
    display: none;
  }
}

.list {
  display: flex;
  flex-direction: column;
  padding: var(--Spacing-spacing-5) 0 var(--Spacing-spacing-5) 0;
}

.list-item {
  align-items: center;
  color: var(--Neutral-UI-neutral-ui-100) !important;
  font-size: var(--Typography-Body);
  font-weight: 600;
  padding: 3px var(--Spacing-spacing-4) 3px var(--Spacing-spacing-4);
  min-height: unset;
  min-width: 240px;
  @include focus-visible;

  &:focus-visible {
    position: relative;
    z-index: 1;
    outline-offset: 0;

    @include screen-lg {
      outline-offset: -4px;
    }
  }

  &--active {
    font-weight: 700;
    background-color: var(--Neutral-UI-neutral-ui-20) !important;
    border-radius: 100px;
    margin: 0 var(--Spacing-spacing-2);
    padding-left: calc(var(--Spacing-spacing-4) - var(--Spacing-spacing-2));
    padding-right: calc(var(--Spacing-spacing-4) - var(--Spacing-spacing-2));
  }
}

.sign-out {
  color: var(--Neutral-UI-neutral-ui-60) !important;
  margin-top: var(--Spacing-spacing-4);
}
</style>
